import { Close as CloseIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import React from 'react';

interface PrivacyModalProps {
  open: boolean;
  onClose: () => void;
}

const PrivacyModal: React.FC<PrivacyModalProps> = ({ open, onClose }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '85vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#178B8E',
          color: '#FFFFFF',
          py: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#FFFFFF' }}>
            🔒 Privacy Policy
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#FFFFFF' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        <Typography variant="body2" sx={{ mb: 4, color: '#5A5A5A' }}>
          This Privacy Policy explains how Happy Paws collects, uses, and protects your personal
          information when you use our platform.
        </Typography>

        {/* Section 1 */}
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#178B8E', mb: 1 }}>
          1. What Information We Collect
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 3 }}>
          <li>
            <Typography variant="body2" sx={{ color: '#5A5A5A' }}>
              Name, email, and contact details
            </Typography>
          </li>
          <li>
            <Typography variant="body2" sx={{ color: '#5A5A5A' }}>
              Pet profiles and sitter information
            </Typography>
          </li>
          <li>
            <Typography variant="body2" sx={{ color: '#5A5A5A' }}>
              Location data provided during registration or search, used solely for sitter matching
              and not tracked passively
            </Typography>
          </li>
          <li>
            <Typography variant="body2" sx={{ color: '#5A5A5A' }}>
              Profile photos and gallery images uploaded to the platform
            </Typography>
          </li>
          <li>
            <Typography variant="body2" sx={{ color: '#5A5A5A' }}>
              Payment and transaction data (processed via Stripe — we do not store card details
              directly)
            </Typography>
          </li>
          <li>
            <Typography variant="body2" sx={{ color: '#5A5A5A' }}>
              App usage data for performance monitoring and feature improvement
            </Typography>
          </li>
        </Box>

        {/* Section 2 */}
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#178B8E', mb: 1 }}>
          2. How We Collect It
        </Typography>
        <Typography variant="body2" sx={{ mb: 1, color: '#5A5A5A' }}>
          We collect data when you:
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 3 }}>
          <li>
            <Typography variant="body2" sx={{ color: '#5A5A5A' }}>
              Register or create a profile
            </Typography>
          </li>
          <li>
            <Typography variant="body2" sx={{ color: '#5A5A5A' }}>
              Fill out forms, upload images, or submit reviews
            </Typography>
          </li>
          <li>
            <Typography variant="body2" sx={{ color: '#5A5A5A' }}>
              Use the app and interact with its features
            </Typography>
          </li>
        </Box>

        {/* Section 3 */}
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#178B8E', mb: 1 }}>
          3. Why We Collect Your Data
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 3 }}>
          <li>
            <Typography variant="body2" sx={{ color: '#5A5A5A' }}>
              To connect pet owners with sitters
            </Typography>
          </li>
          <li>
            <Typography variant="body2" sx={{ color: '#5A5A5A' }}>
              To ensure platform safety and account integrity
            </Typography>
          </li>
          <li>
            <Typography variant="body2" sx={{ color: '#5A5A5A' }}>
              To provide customer support
            </Typography>
          </li>
          <li>
            <Typography variant="body2" sx={{ color: '#5A5A5A' }}>
              To improve app performance and features
            </Typography>
          </li>
          <li>
            <Typography variant="body2" sx={{ color: '#5A5A5A' }}>
              To send notifications or promotions (with your consent)
            </Typography>
          </li>
        </Box>

        {/* Section 4 */}
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#178B8E', mb: 1 }}>
          4. Third-Party Services We Use
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 3 }}>
          <li>
            <Typography variant="body2" sx={{ color: '#5A5A5A' }}>
              <strong>Supabase</strong> — database, authentication, and file storage
            </Typography>
          </li>
          <li>
            <Typography variant="body2" sx={{ color: '#5A5A5A' }}>
              <strong>Stripe</strong> — payment processing and sitter payouts
            </Typography>
          </li>
          <li>
            <Typography variant="body2" sx={{ color: '#5A5A5A' }}>
              <strong>Google Maps API</strong> — location search and sitter proximity features
            </Typography>
          </li>
        </Box>

        {/* Section 5 */}
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#178B8E', mb: 1 }}>
          5. Your Rights
        </Typography>
        <Typography variant="body2" sx={{ mb: 1, color: '#5A5A5A' }}>
          In accordance with applicable data protection law, including GDPR where relevant, you have
          the right to:
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 3 }}>
          <li>
            <Typography variant="body2" sx={{ color: '#5A5A5A' }}>
              Access or update your personal information
            </Typography>
          </li>
          <li>
            <Typography variant="body2" sx={{ color: '#5A5A5A' }}>
              Request deletion of your account and associated data
            </Typography>
          </li>
          <li>
            <Typography variant="body2" sx={{ color: '#5A5A5A' }}>
              Withdraw consent at any time
            </Typography>
          </li>
          <li>
            <Typography variant="body2" sx={{ color: '#5A5A5A' }}>
              Contact us with any privacy concerns
            </Typography>
          </li>
        </Box>

        {/* Section 6 */}
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#178B8E', mb: 1 }}>
          6. Data Security
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: '#5A5A5A', lineHeight: 1.7 }}>
          We use modern security practices to protect your data, including encrypted connections
          (HTTPS), hashed passwords, and restricted admin access.
        </Typography>

        {/* Section 7 */}
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#178B8E', mb: 1 }}>
          7. Children's Privacy
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: '#5A5A5A', lineHeight: 1.7 }}>
          Happy Paws is not intended for users under the age of 13. We do not knowingly collect data
          from children.
        </Typography>

        {/* Section 8 */}
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#178B8E', mb: 1 }}>
          8. Changes to This Policy
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: '#5A5A5A', lineHeight: 1.7 }}>
          This policy may be updated occasionally. Any changes will be posted on this page with the
          updated date.
        </Typography>

        {/* Section 9 */}
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#178B8E', mb: 1 }}>
          9. Contact Us
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: '#5A5A5A', lineHeight: 1.7 }}>
          If you have questions about this policy, contact us at{' '}
          <a
            href="mailto:support@happypaws.com"
            style={{ color: '#178B8E', textDecoration: 'none' }}
          >
            support@happypaws.com
          </a>
          .
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            backgroundColor: '#178B8E',
            color: '#FFFFFF',
            px: 4,
            '&:hover': {
              backgroundColor: '#0F6164',
            },
          }}
        >
          I Understand
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrivacyModal;
